import dayjs, { type Dayjs } from '@/utilities/dayjs-config'
import { Meal, MEAL_TYPES } from '@/models/meal'
import type {
  Meal as PopsMeal,
  SubscriptionWeekMealsResponse as PopsSubscriptionWeekMealsResponse,
} from '@/types/pops'
import { $mealSwaps } from '@/stores/meal-program-subscription/stores'

type GroupedMenu = {
  date: Dayjs
  meals: Meal[]
}

export class SubscriptionWeekMenu {
  menuDays: { [key: string]: { [key: string]: Meal } }
  numberOfDaysWithFood: number
  week: Dayjs
  #menuGroupedByDate: any

  constructor(
    week: Dayjs,
    rawSubscriptionWeekMenu: PopsSubscriptionWeekMealsResponse,
  ) {
    // Create meal objects
    this.menuDays = Object.fromEntries(
      Object.entries(rawSubscriptionWeekMenu).map(([key, value]) => {
        const meals = MEAL_TYPES.reduce(
          (acc, type) => {
            if (!value[type]) {
              return acc
            }

            acc[type] = new Meal(value[type] as PopsMeal)
            return acc
          },
          {} as { [key: string]: Meal },
        )

        return [key, meals]
      }),
    )

    this.numberOfDaysWithFood = Object.keys(this.menuDays).length
    this.week = week
  }

  get mealSwaps() {
    return $mealSwaps.get()[this.week.format('YYYY-MM-DD')] || []
  }

  /**
   * Builds the menu for the days of food, meals, & meal swaps
   * of this subscription week.
   *
   * @example
   * [
   *   {
   *     "date": dayjs(),
   *     "meals": [
   *       {
   *         "meal": Meal,
   *         "swapped_meal": Meal
   *       },
   *       {
   *         "meal": Meal,
   *         "swapped_meal": Meal
   *       }
   *     ]
   *   },
   *   ...
   * ]
   */
  get menuGroupedByDate() {
    if (this.#menuGroupedByDate) return this.#menuGroupedByDate

    this.#menuGroupedByDate = [0, 1, 2, 3, 4, 5, 6].reduce((acc, offset) => {
      const menuDayDate = dayjs(this.week).add(offset, 'day')
      const menuDayDateKey = menuDayDate.format('YYYY-MM-DD')
      const menuDay = this.menuDays[menuDayDateKey]

      if (!menuDay) return acc

      acc.push({
        date: menuDayDate,
        meals: MEAL_TYPES.reduce((meals, type) => {
          const meal = menuDay[type]

          if (!meal) return meals

          meals.push(meal)

          return meals
        }, [] as Meal[]),
      })

      return acc
    }, [] as GroupedMenu[])

    return this.#menuGroupedByDate
  }
}
